export interface PagePointerSnapshot {
  x: number;
  y: number;
  clicking: boolean;
  reducedMotion: boolean;
}

const STEP_INTERVAL_MS = 800;
export const PAGE_POINTER_MOVE_MS = 500;

/** 页面任务沿真实控件执行；每一步前重新确认可见性和用户接管状态。 */
export function createPagePointer(doc: Document, track: (event: string, data: unknown) => void) {
  let snapshot: PagePointerSnapshot | null = null;
  let generation = 0;
  let expectedPath: string | undefined;
  let disposed = false;
  let tail = Promise.resolve();
  let releaseWait: (() => void) | undefined;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  const publish = (value: PagePointerSnapshot | null) => {
    snapshot = value;
    listeners.forEach((listener) => listener());
  };
  const cancel = () => {
    generation++;
    expectedPath = undefined;
    clearTimeout(idleTimer);
    releaseWait?.();
    if (snapshot) track('agent.pointer.cancelled', { reason: 'interrupted' });
    publish(null);
  };
  const usable = (element: HTMLElement) => {
    if (!element.isConnected || doc.visibilityState === 'hidden' || !element.getClientRects().length)
      return false;
    if (
      element.closest('[hidden],[inert],[aria-hidden="true"],[aria-disabled="true"]') ||
      element.matches(':disabled,[readonly]')
    )
      return false;
    const style = doc.defaultView?.getComputedStyle(element);
    return style?.display !== 'none' && style?.visibility !== 'hidden';
  };
  const wait = (ms: number) => new Promise<void>((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      if (releaseWait === finish) releaseWait = undefined;
      resolve();
    };
    const timer = setTimeout(finish, ms);
    releaseWait = finish;
  });
  const point = (element: HTMLElement, effect: () => void | Promise<void>, clicking = true) => {
    const owner = generation;
    const run = async () => {
      clearTimeout(idleTimer);
      idleTimer = undefined;
      const startedAt = Date.now();
      const current = () => !disposed && owner === generation && usable(element);
      if (!current()) return false;
      const reducedMotion = doc.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      element.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      const rect = element.getBoundingClientRect();
      const x = rect.left + Math.min(24, rect.width / 2);
      const y = rect.top + rect.height / 2;
      if (!snapshot) publish({ x: Math.max(0, x - 64), y: y + 40, clicking: false, reducedMotion });
      await wait(16);
      if (!current()) return false;
      publish({ x, y, clicking: false, reducedMotion });
      await wait(reducedMotion ? 0 : PAGE_POINTER_MOVE_MS);
      if (!current()) return false;
      const next = element.getBoundingClientRect();
      publish({
        x: next.left + Math.min(24, next.width / 2),
        y: next.top + next.height / 2,
        clicking,
        reducedMotion,
      });
      await wait(reducedMotion ? 0 : 40);
      if (!current()) return false;
      try {
        await effect();
        await wait(reducedMotion ? 0 : 40);
        if (owner !== generation) return false;
        if (snapshot) publish({ ...snapshot, clicking: false });
        await wait(reducedMotion ? 0 : Math.max(0, STEP_INTERVAL_MS - (Date.now() - startedAt)));
        return owner === generation;
      } finally {
        if (owner === generation)
          idleTimer = setTimeout(() => publish(null), reducedMotion ? 0 : 160);
      }
    };
    const result = tail.then(run);
    tail = result.then(() => undefined, () => undefined);
    return result;
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if ((!event.key || event.key === 'Unidentified') && !event.code && !event.keyCode && !event.isComposing)
      return;
    cancel();
  };
  const onVisibility = () => { if (doc.visibilityState === 'hidden') cancel(); };
  doc.addEventListener('pointerdown', cancel, true);
  doc.addEventListener('keydown', onKeyDown, true);
  doc.addEventListener('visibilitychange', onVisibility);
  return {
    port: { cancel, point },
    hover: (element: HTMLElement, effect: () => void) => point(element, effect, false),
    expectNavigation(path?: string) { expectedPath = path; },
    onPathChange(path: string) {
      if (expectedPath && path === expectedPath) {
        expectedPath = undefined;
      } else cancel();
    },
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    dispose() {
      disposed = true;
      cancel();
      listeners.clear();
      doc.removeEventListener('pointerdown', cancel, true);
      doc.removeEventListener('keydown', onKeyDown, true);
      doc.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
